/**
 * Vehicle catalog — refonte F3/F4 (04/02/2026).
 *
 * Changements clés :
 *  - `specialties[]` normalisé sur les 8 axes du doc « Analyse catalogue
 *    flotte » : Messagerie/Colis, Frigo/Pharma, BTP/Matériaux/Engins TP,
 *    ADR/Produits sensibles, Export UE, Vrac/Palettes, Transport animaux,
 *    ZFE.
 *  - `unlockedIf` reste un libellé descriptif pour l'UI.
 *  - `unlockChecks` (nouveau) : objet déclaratif consommé par Fleet.canUnlock :
 *      { minMonths?: number, requiresInvestments?: string[], minRevenue?: number }
 *    Le « capacité de financement » (treasury >= price) est vérifié à
 *    l'extérieur, comme avant.
 *
 * Le doc demande explicitement :
 *  - mantor-springo-xl : retirer la condition de trésorerie (« je n'en mettrais pas »)
 *  - Tier 2+ : dépendance Quai + Entrepôt
 *  - Tier 3+ : Entrepôt grand gabarit (inv-depot-08) au lieu de seuils CA arbitraires
 *  - Bétaillère : Point d'eau (inv-depot-09) en plus du quai renforcé
 *  - ADR : Zone ADR sécurisée (inv-depot-10) en plus du quai renforcé
 *  - Électrique : Borne de recharge (inv-depot-11) en plus du grand gabarit
 */
export const VEHICLES = [
  // TIER 1 — Fourgons légers (< 3,5T)
  {
    id: 'valrek-maxor-l2h2',
    brand: 'VALREK', model: 'Maxor L2H2', tier: 1,
    image: '/vehicles/valrek-maxor-l2h2.webp',
    ptac: '3,5T', payload: 1200, volume: 11, power: 135,
    consumption: 10.5, kmPerDay: 180,
    specialties: ['Messagerie/Colis'],
    price: 22000, leasePerMonth: 680, maintenancePerMonth: 160,
    insurancePerMonth: 190, dailyFuel: 35,
    depotRequired: 'Box standard', license: 'B',
    unlockedIf: 'Départ',
    unlockChecks: {},
    advantage: 'Économique, polyvalent, permis B',
    rating: 4,
  },
  {
    id: 'mantor-springo-xl',
    brand: 'MANTOR', model: 'Springo XL', tier: 1,
    image: '/vehicles/mantor-springo-xl.webp',
    ptac: '3,5T', payload: 1050, volume: 14, power: 190,
    consumption: 11.5, kmPerDay: 200,
    specialties: ['Messagerie/Colis'],
    price: 28000, leasePerMonth: 820, maintenancePerMonth: 200,
    insurancePerMonth: 210, dailyFuel: 40,
    depotRequired: 'Box standard', license: 'B',
    unlockedIf: 'Départ',
    unlockChecks: {},
    advantage: 'Plus rapide, image premium',
    rating: 3,
  },
  {
    id: 'valrek-maxor-frigo',
    brand: 'VALREK', model: 'Maxor Frigo', tier: 1,
    image: '/vehicles/valrek-maxor-frigo.webp',
    ptac: '3,5T', payload: 900, volume: 8, power: 135,
    consumption: 13, kmPerDay: 160,
    specialties: ['Messagerie/Colis', 'Frigo/Pharma'],
    price: 35000, leasePerMonth: 980, maintenancePerMonth: 240,
    insurancePerMonth: 230, dailyFuel: 48,
    depotRequired: 'Zone frigo dépôt', license: 'B',
    unlockedIf: 'Zone frigorifique active',
    unlockChecks: { requiresInvestments: ['inv-depot-05'] },
    advantage: 'Débloque contrats alimentaires',
    rating: 3,
  },
  // TIER 2 — Porteurs (7T–12T)
  // 05/02/2026 — Q1 retour user prod : différenciation T2.
  // - Daily 70C : repositionné sur l'URBAIN (Messagerie/Colis + Vrac/Palettes)
  //   pour combler le gap entre T1 (3.5T messagerie) et T2 industriel.
  // - Eurocargo 120E : conservé en BTP/industriel lourd exclusif.
  // - Mantor TGA Frigo : spécialités réduites à Frigo/Pharma uniquement
  //   (auparavant cumulé BTP + Vrac à tort selon retour user).
  {
    id: 'ivectra-daily-70c',
    brand: 'IVECTRA', model: 'Daily 70C 7T', tier: 2,
    image: '/vehicles/ivectra-daily-70c.webp',
    ptac: '7,0T', payload: 3500, volume: 18, power: 170,
    consumption: 14, kmPerDay: 220,
    specialties: ['Messagerie/Colis', 'Vrac/Palettes'],
    price: 48000, leasePerMonth: 1200, maintenancePerMonth: 350,
    insurancePerMonth: 320, dailyFuel: 57,
    depotRequired: 'Entrepôt + quai', license: 'C',
    unlockedIf: "1 mois d'activité + Entrepôt + Quai dédié",
    unlockChecks: { minMonths: 1, requiresInvestments: ['inv-depot-02', 'inv-depot-04'] },
    advantage: 'Distribution urbaine express — zones piétonnes & ZFE compatibles',
    rating: 4,
  },
  {
    id: 'mantor-tga-frigo-7t',
    brand: 'MANTOR', model: 'TGA Frigo 7T', tier: 2,
    image: '/vehicles/mantor-tga-frigo-7t.webp',
    ptac: '7,5T', payload: 3200, volume: 16, power: 150,
    consumption: 16, kmPerDay: 200,
    specialties: ['Frigo/Pharma'],
    price: 62000, leasePerMonth: 1550, maintenancePerMonth: 420,
    insurancePerMonth: 380, dailyFuel: 55,
    depotRequired: 'Entrepôt + quai + zone frigo', license: 'C',
    unlockedIf: 'Entrepôt + Quai + Zone frigo actifs',
    unlockChecks: { requiresInvestments: ['inv-depot-02', 'inv-depot-04', 'inv-depot-05'] },
    advantage: 'Débloque GMS et pharma régionaux',
    rating: 3,
  },
  {
    id: 'ivectra-eurocargo-120e',
    brand: 'IVECTRA', model: 'Eurocargo 120E', tier: 2,
    image: '/vehicles/ivectra-eurocargo-120e.webp',
    ptac: '12,0T', payload: 7000, volume: 24, power: 220,
    consumption: 18, kmPerDay: 250,
    specialties: ['BTP/Matériaux/Engins TP'],
    price: 75000, leasePerMonth: 1800, maintenancePerMonth: 480,
    insurancePerMonth: 420, dailyFuel: 83,
    depotRequired: 'Entrepôt + quai renforcé', license: 'C',
    unlockedIf: "1 mois d'activité + Entrepôt + Quai dédié",
    unlockChecks: { minMonths: 1, requiresInvestments: ['inv-depot-02', 'inv-depot-04'] },
    advantage: 'BTP/industrie lourde — chantiers majeurs et matériaux bruts',
    rating: 4,
  },
  // TIER 3 — Semi-remorques (44T)
  // 05/02/2026 — Q1 retour user prod : Volvax FX 460 & Dafora XF 480
  // supprimés (doublons de Scanex R 500). Le T3 export prestige conserve
  // uniquement Scanex R 500. Les véhicules T3 spécialisés (Frigo Volvax,
  // Benne Mantor) restent en place car leur niche est distincte.
  {
    id: 'scanex-r500-highline',
    brand: 'SCANEX', model: 'R 500 Highline', tier: 3,
    image: '/vehicles/scanex-r500-highline.webp',
    ptac: '44,0T', payload: 25000, volume: 34, power: 500,
    consumption: 33, kmPerDay: 650,
    specialties: ['Export UE', 'Vrac/Palettes'],
    price: 115000, leasePerMonth: 3200, maintenancePerMonth: 950,
    insurancePerMonth: 820, dailyFuel: 398,
    depotRequired: 'Entrepôt + quai', license: 'CE',
    unlockedIf: 'Entrepôt + Quai + capacité de financement',
    unlockChecks: { requiresInvestments: ['inv-depot-02', 'inv-depot-04'] },
    advantage: 'Puissance export, image prestige',
    rating: 5,
  },
  {
    id: 'volvax-fh-frigo-lt',
    brand: 'VOLVAX', model: 'FH Frigo LongTrax', tier: 3,
    image: '/vehicles/volvax-fh-frigo-lt.webp',
    ptac: '44,0T', payload: 23500, volume: 32, power: 460,
    consumption: 36, kmPerDay: 580,
    specialties: ['Frigo/Pharma', 'Export UE', 'Vrac/Palettes'],
    price: 108000, leasePerMonth: 3050, maintenancePerMonth: 950,
    insurancePerMonth: 800, dailyFuel: 395,
    depotRequired: 'Entrepôt + quai + zone frigo', license: 'CE',
    unlockedIf: 'Entrepôt + Quai + Zone frigo + capacité de financement',
    unlockChecks: { requiresInvestments: ['inv-depot-02', 'inv-depot-04', 'inv-depot-05'] },
    advantage: 'Frigorifique grande distance — débloque les AO GMS régionaux',
    rating: 5,
  },
  {
    id: 'mantor-actrix-benne-44t',
    brand: 'MANTOR', model: 'Actrix Benne 44T', tier: 3,
    image: '/vehicles/mantor-actrix-benne-44t.webp',
    ptac: '44,0T', payload: 26000, volume: 30, power: 510,
    consumption: 34, kmPerDay: 540,
    specialties: ['BTP/Matériaux/Engins TP', 'Export UE', 'Vrac/Palettes'],
    price: 102000, leasePerMonth: 2900, maintenancePerMonth: 1050,
    insurancePerMonth: 780, dailyFuel: 380,
    depotRequired: 'Entrepôt + quai renforcé', license: 'CE',
    unlockedIf: 'Entrepôt + Quai renforcé + capacité de financement',
    unlockChecks: { requiresInvestments: ['inv-depot-02', 'inv-depot-07'] },
    advantage: 'Benne 44T — chantiers majeurs et lots routiers BTP',
    rating: 5,
  },
  // TIER 4 — Véhicules spécialisés
  {
    id: 'volvax-fx-frigo-arctic',
    brand: 'VOLVAX', model: 'FX Frigo Arctic', tier: 4,
    image: '/vehicles/volvax-fx-frigo-arctic.webp',
    ptac: '44,0T', payload: 22000, volume: 32, power: 460,
    consumption: 35, kmPerDay: 550,
    specialties: ['Frigo/Pharma', 'Export UE'],
    price: 130000, leasePerMonth: 3600, maintenancePerMonth: 1200,
    insurancePerMonth: 950, dailyFuel: 358,
    depotRequired: 'Entrepôt + quai + zone frigo', license: 'CE',
    unlockedIf: 'Entrepôt + Quai + Zone frigo + capacité de financement',
    unlockChecks: { requiresInvestments: ['inv-depot-02', 'inv-depot-04', 'inv-depot-05'] },
    advantage: 'Débloque pharma et agroalim. premium',
    rating: 5,
  },
  // 05/02/2026 — Q1 retour user prod : véhicule Bétaillère (animaux vivants)
  // retiré. Investissement Point d'eau, contrat bovin, spécialité « Transport
  // animaux » et formation `specTransportAnimaux` également supprimés.
  {
    id: 'mantor-actrix-adr',
    brand: 'MANTOR', model: 'Actrix ADR', tier: 4,
    image: '/vehicles/mantor-actrix-adr.webp',
    ptac: '44,0T', payload: 23000, volume: 0, power: 510,
    consumption: 34, kmPerDay: 500,
    specialties: ['ADR/Produits sensibles', 'Export UE'],
    price: 160000, leasePerMonth: 4200, maintenancePerMonth: 1300,
    insurancePerMonth: 1400, dailyFuel: 316,
    depotRequired: 'Entrepôt + Quai renforcé + Zone ADR sécurisée', license: 'CE',
    unlockedIf: 'Entrepôt + Quai renforcé + Zone ADR sécurisée + capacité de financement',
    unlockChecks: { requiresInvestments: ['inv-depot-02', 'inv-depot-07', 'inv-depot-10'] },
    advantage: 'Contrats chimie/pétrole très rentables',
    rating: 4,
  },
  // TIER 5 — Prestige & Export UE
  {
    id: 'scanex-r650-v8-prestige',
    brand: 'SCANEX', model: 'R 650 V8 Prestige', tier: 5,
    image: '/vehicles/scanex-r650-v8-prestige.webp',
    ptac: '44,0T', payload: 25000, volume: 34, power: 650,
    consumption: 36, kmPerDay: 750,
    specialties: ['Export UE', 'Vrac/Palettes'],
    price: 185000, leasePerMonth: 4800, maintenancePerMonth: 1400,
    insurancePerMonth: 1100, dailyFuel: 499,
    depotRequired: 'Entrepôt grand gabarit + Quai renforcé', license: 'CE',
    unlockedIf: 'Entrepôt grand gabarit + Quai renforcé + capacité de financement',
    unlockChecks: { requiresInvestments: ['inv-depot-07', 'inv-depot-08'] },
    advantage: 'Le plus rapide, image maximale',
    rating: 5,
  },
  {
    id: 'dafora-xf-porte-engins',
    brand: 'DAFORA', model: 'XF Porte-Engins', tier: 5,
    image: '/vehicles/dafora-xf-porte-engins.webp',
    ptac: '44,0T', payload: 30000, volume: 0, power: 530,
    consumption: 38, kmPerDay: 350,
    specialties: ['BTP/Matériaux/Engins TP', 'Export UE'],
    price: 175000, leasePerMonth: 4500, maintenancePerMonth: 1350,
    insurancePerMonth: 1300, dailyFuel: 247,
    depotRequired: 'Entrepôt grand gabarit + Quai renforcé', license: 'CE',
    unlockedIf: 'Entrepôt grand gabarit + Quai renforcé + capacité de financement',
    unlockChecks: { requiresInvestments: ['inv-depot-07', 'inv-depot-08'] },
    advantage: 'Contrats BTP les plus rentables',
    rating: 4,
  },
  {
    id: 'volvax-fx-electric',
    brand: 'VOLVAX', model: 'FX Electric ZeroLoad', tier: 5,
    image: '/vehicles/volvax-fx-electric.webp',
    engineType: 'electric',
    ptac: '44,0T', payload: 20000, volume: 33, power: 490,
    consumption: 1.8, kmPerDay: 300,
    specialties: ['ZFE', 'Export UE', 'Vrac/Palettes'],
    price: 220000, leasePerMonth: 5200, maintenancePerMonth: 600,
    insurancePerMonth: 980, dailyFuel: 55,
    depotRequired: 'Entrepôt grand gabarit + Quai renforcé + Borne de recharge', license: 'CE',
    unlockedIf: 'Entrepôt grand gabarit + Quai renforcé + Borne de recharge + capacité de financement',
    unlockChecks: { requiresInvestments: ['inv-depot-07', 'inv-depot-08', 'inv-depot-11'] },
    advantage: 'ZFE illimité, coût énergie x6 moins cher',
    rating: 5,
  },
  // 05/02/2026 — Q1 retour user prod : convoi exceptionnel (éoliennes,
  // fusée, maison préfab). Niche prestige avec arc narratif :
  //   • Pas de prérequis dépôt/atelier (le tracteur est livré directement
  //     sur site par le fabricant).
  //   • Permis CE + investissement « Permis convoi exceptionnel » obligatoire.
  //   • Chaque contrat exige un intérim spécialisé ponctuel (3 000-5 000 €)
  //     pour le second chauffeur + voiture pilote.
  //   • Récompenses 10 000-25 000 € par mission (variable selon volume).
  //   • Risques d'aléas importants : panne, météo, accident → narrative
  //     généré dans `convoyExceptionnelSlice.js`.
  {
    id: 'mantor-heavyhaul-xxl',
    brand: 'MANTOR', model: 'Heavyhaul XXL', tier: 5,
    image: '/vehicles/mantor-heavyhaul-xxl.webp',
    ptac: '85,0T', payload: 60000, volume: 0, power: 750,
    consumption: 52, kmPerDay: 180,
    specialties: ['Convoi exceptionnel', 'BTP/Matériaux/Engins TP'],
    price: 285000, leasePerMonth: 6500, maintenancePerMonth: 2200,
    insurancePerMonth: 1900, dailyFuel: 380,
    depotRequired: 'Aucun prérequis dépôt — livraison directe', license: 'CE',
    unlockedIf: "Permis Convoi exceptionnel actif (cf. Fonctions support)",
    unlockChecks: { requiresInvestments: ['inv-permit-exceptional'] },
    advantage: 'Éoliennes, fusées, maisons préfab — récompenses 10K-25K €/mission, risques élevés',
    rating: 5,
  },
];

export const getVehicleById = (id) => VEHICLES.find(v => v.id === id);
export const getVehiclesByTier = (tier) => VEHICLES.filter(v => v.tier === tier);
